import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('dispatcher');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(name, email, password, role);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-[#0066CC]/5 via-white to-[#FF6600]/5">
      {/* Main Container */}
      <div className="w-full max-w-md">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          {/* Logo */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#0066CC] to-[#004499] shadow-xl mb-4">
            <span className="text-white font-black text-4xl tracking-tight">B</span>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-black text-[#0066CC] font-['Manrope'] leading-tight mb-1">
            Bylyngo
          </h1>
          <h2 className="text-lg font-semibold text-gray-600 font-['Manrope']">
            Language Service Provider Platform
          </h2>
          
          {/* Accent line */}
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="h-1 w-8 bg-[#0066CC] rounded-full"></div>
            <div className="h-1 w-6 bg-[#FF6600] rounded-full"></div>
            <div className="h-1 w-8 bg-[#0066CC] rounded-full"></div>
          </div>
        </div>

        {/* Register Card */}
        <Card className="border-0 shadow-2xl shadow-[#0066CC]/10">
          <CardContent className="p-8">
            <h3 className="text-xl font-semibold text-center mb-6 text-gray-800">
              Create your account
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-11 h-12 border-2 focus:border-[#0066CC] transition-colors"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 border-2 focus:border-[#0066CC] transition-colors"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 border-2 focus:border-[#0066CC] transition-colors"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-gray-700">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-12 border-2 focus:border-[#0066CC]" data-testid="role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="dispatcher">Dispatcher / Scheduling</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                    <SelectItem value="accounting">Accounting / AP</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#0066CC] hover:bg-[#0055AA] text-lg font-semibold shadow-lg shadow-[#0066CC]/30 transition-all hover:shadow-xl hover:shadow-[#0066CC]/40"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0066CC] hover:text-[#0055AA] font-semibold transition-colors" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Creator Credit */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">Created by</p>
          <p className="text-base font-semibold text-[#FF6600]">Dawn Pérez Maldonado</p>
        </div>

        {/* Footer */}
        <p className="mt-4 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} Bylyngo Language Services. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Register;
